import type { Node, NodeKind } from '@menuvera/engine';

import { newNodeId } from './nodeTree';

export interface NodePaletteEntry {
  kind: NodeKind;
  label: string;
  hint: string;
}

/**
 * The insert palette.
 *
 * Ordered by how often a menu page actually needs them rather than
 * alphabetically, so the two that matter — a column and a repeater — are the
 * first thing an author reaches for.
 */
export const NODE_PALETTE: NodePaletteEntry[] = [
  { kind: 'stack', label: 'Column / row', hint: 'A column or row of blocks' },
  { kind: 'repeater', label: 'Dish list', hint: 'Repeats once per dish or category' },
  { kind: 'text', label: 'Text', hint: 'Name, price or heading' },
  { kind: 'image', label: 'Image', hint: 'Photo, logo or artwork' },
  { kind: 'grid', label: 'Columns', hint: 'Two or more columns' },
  { kind: 'divider', label: 'Line', hint: 'Rule between sections' },
  { kind: 'spacer', label: 'Gap', hint: 'Empty space' },
  { kind: 'shape', label: 'Shape', hint: 'Colour block' },
  { kind: 'frame', label: 'Frame', hint: 'Box that can be a wrapper, category or dish' },
  { kind: 'group', label: 'Group', hint: 'Styling wrapper' },
  { kind: 'slot', label: 'Content hole', hint: 'Named hole for page content' },
  { kind: 'overlay', label: 'Decoration', hint: 'Freely placed decoration' },
];

export function createNode(kind: NodeKind): Node {
  const id = newNodeId(kind.slice(0, 3));

  switch (kind) {
    case 'stack':
      return { id, kind, direction: 'column', gap: '$md', children: [] };
    case 'grid':
      return { id, kind, columns: 2, gap: '$md', children: [] };
    case 'group':
      return { id, kind, children: [] };
    case 'frame':
      return { id, kind, entity: 'wrapper', children: [] };
    case 'overlay':
      return { id, kind, children: [] };
    case 'repeater':
      return {
        id,
        kind,
        source: 'page.items',
        as: 'item',
        itemTemplate: {
          id: newNodeId('sta'),
          kind: 'stack',
          direction: 'row',
          gap: '$sm',
          justify: 'between',
          children: [
            {
              id: newNodeId('txt'),
              kind: 'text',
              source: { template: '{{item.name}}' },
              typography: 'itemName',
              color: 'onSurface',
            },
            {
              id: newNodeId('txt'),
              kind: 'text',
              source: { template: '{{item.price}}', format: 'price' },
              typography: 'price',
              color: 'onSurface',
            },
          ],
        },
      };
    case 'slot':
      return { id, kind, slotId: 'content', accepts: ['any'] };
    case 'text':
      return {
        id,
        kind,
        source: { template: 'Text' },
        typography: 'itemName',
        color: 'onSurface',
      };
    case 'image':
      return {
        id,
        kind,
        // Root-safe default: `item.image` is only valid inside an item repeater
        // and would immediately fail document validation on a blank page.
        source: { binding: 'restaurant.logo', placeholder: 'silhouette' },
        fit: 'cover',
        aspect: 1.4,
        layout: { width: 200 },
      };
    case 'shape':
      return {
        id,
        kind,
        shape: 'rect',
        fill: 'accent',
        layout: { width: 200, height: 120 },
      };
    case 'divider':
      return { id, kind, orientation: 'horizontal', color: 'onSurfaceMuted', thickness: 1 };
    case 'spacer':
      return { id, kind, size: '$md' };
    default: {
      const exhaustive: never = kind;
      throw new Error(`Unknown node kind: ${String(exhaustive)}`);
    }
  }
}
